import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { ChartLineUp, TrendUp, Warning, Users, Star, Buildings } from "@phosphor-icons/react";

const BAND_META = {
  exceptional: { label: "Exceptional", tone: "bg-emerald-100 border-emerald-300 text-emerald-800", threshold: "≥ 4.5" },
  high: { label: "High", tone: "bg-emerald-50 border-emerald-200 text-emerald-700", threshold: "3.75 – 4.5" },
  solid: { label: "Solid", tone: "bg-blue-50 border-blue-200 text-blue-700", threshold: "3.0 – 3.75" },
  development: { label: "Needs development", tone: "bg-amber-50 border-amber-200 text-amber-700", threshold: "2.0 – 3.0" },
  critical: { label: "Critical", tone: "bg-red-50 border-red-200 text-red-700", threshold: "< 2.0" },
};

function ScoreBadge({ score }) {
  let tone = "bg-zinc-100 text-zinc-600 border-zinc-200";
  if (score >= 4.5) tone = BAND_META.exceptional.tone;
  else if (score >= 3.75) tone = BAND_META.high.tone;
  else if (score >= 3.0) tone = BAND_META.solid.tone;
  else if (score >= 2.0) tone = BAND_META.development.tone;
  else if (score >= 0) tone = BAND_META.critical.tone;
  return <span className={`inline-block px-2 py-0.5 rounded-sm border font-mono-plex text-xs font-bold ${tone}`}>{score?.toFixed(2) ?? "—"}</span>;
}

function DeptBar({ row, max }) {
  const width = max ? Math.round((row.average / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <div className="w-40 text-sm truncate">{row.department}</div>
      <div className="flex-1 h-3 bg-zinc-100 rounded-sm overflow-hidden">
        <div className="h-full bg-gradient-to-r from-zenith-teal to-zenith-magenta" style={{ width: `${width}%` }} />
      </div>
      <div className="w-24 text-right text-xs font-mono-plex">
        <ScoreBadge score={row.average} />
        <span className="text-zinc-400 ml-1">·{row.count}</span>
      </div>
    </div>
  );
}


export default function KPIDashboard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try { const r = await api.get("/kpi-scoring/overview"); setData(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  useEffect(() => { load(); }, []);

  const maxDept = useMemo(() => Math.max(...(data?.by_department || []).map(r => r.average), 5), [data]);
  const bandTotal = useMemo(() => Object.values(data?.bands || {}).reduce((a, b) => a + b, 0), [data]);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-zenith-teal font-bold">KPI · Performance Scoring</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">How is the company performing?</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">Aggregates every employee's weighted KRA rating into a single score, groups people into performance bands, and surfaces top talent + at-risk employees.</p>
      </div>

      {busy && <div className="text-xs text-zinc-500">Loading…</div>}

      {data && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard label="Employees scored" value={data.count} icon={Users} tone="teal" />
            <SummaryCard label="Company avg" value={data.average?.toFixed(2)} icon={ChartLineUp} tone="magenta" sub="out of 5" />
            <SummaryCard label="Top performers" value={data.top_performers.length} icon={Star} tone="green" />
            <SummaryCard label="Needs attention" value={data.improvement_needed.length} icon={Warning} tone="blue" />
          </div>

          {/* Bands */}
          <div className="border border-zinc-200 rounded-sm bg-white p-4">
            <div className="font-display font-bold text-adb-navy mb-3">Performance bands</div>
            {bandTotal === 0 ? (
              <div className="text-xs text-zinc-500 border border-dashed border-zinc-200 rounded-sm p-6 text-center">
                No KRA ratings yet. Go to Performance → KRA and rate this cycle's KRAs to populate this dashboard.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {Object.entries(BAND_META).map(([k, meta]) => {
                  const cnt = data.bands[k] || 0;
                  const pct = bandTotal ? Math.round((cnt / bandTotal) * 100) : 0;
                  return (
                    <div key={k} data-testid={`band-${k}`} className={`border rounded-sm p-3 ${meta.tone}`}>
                      <div className="text-[10px] uppercase tracking-widest font-bold">{meta.label}</div>
                      <div className="font-display text-2xl font-bold">{cnt}</div>
                      <div className="text-[10px] mt-0.5">{pct}% · {meta.threshold}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Department averages */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-zinc-200 rounded-sm bg-white p-4">
              <div className="font-display font-bold text-adb-navy mb-2 flex items-center gap-2"><Buildings size={14} weight="duotone" className="text-zenith-teal" /> Department averages</div>
              {(data.by_department || []).length === 0 ? (
                <div className="text-xs text-zinc-500">No data yet.</div>
              ) : data.by_department.map(r => <DeptBar key={r.department} row={r} max={maxDept} />)}
            </div>
            <div className="border border-zinc-200 rounded-sm bg-white p-4">
              <div className="font-display font-bold text-adb-navy mb-2 flex items-center gap-2"><Star size={14} weight="fill" className="text-zenith-magenta" /> Top performers</div>
              {(data.top_performers || []).length === 0 ? (
                <div className="text-xs text-zinc-500">No data yet.</div>
              ) : (
                <div className="space-y-1">
                  {data.top_performers.map(r => (
                    <div key={r.employee_id} data-testid={`top-${r.employee_id}`} className="flex items-center gap-3 border border-zinc-100 rounded-sm p-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{r.employee_name || r.employee_id}</div>
                        <div className="text-[10px] text-zinc-500">{r.department || "—"} · {r.designation || ""}</div>
                      </div>
                      <ScoreBadge score={r.score} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Needs attention */}
          {(data.improvement_needed || []).length > 0 && (
            <div className="border border-red-200 bg-red-50 rounded-sm p-4">
              <div className="font-display font-bold text-red-700 mb-2 flex items-center gap-2"><Warning size={14} /> Needs attention</div>
              <div className="grid md:grid-cols-2 gap-1">
                {data.improvement_needed.map(r => (
                  <div key={r.employee_id} className="flex items-center gap-3 border border-red-100 bg-white rounded-sm p-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{r.employee_name || r.employee_id}</div>
                      <div className="text-[10px] text-zinc-500">{r.department || "—"}</div>
                    </div>
                    <ScoreBadge score={r.score} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}


const TONE = {
  teal: { bg: "bg-zenith-teal/10", border: "border-zenith-teal/30", icon: "text-zenith-teal-700" },
  magenta: { bg: "bg-zenith-magenta/10", border: "border-zenith-magenta/30", icon: "text-zenith-magenta" },
  green: { bg: "bg-zenith-green/10", border: "border-zenith-green/30", icon: "text-zenith-green" },
  blue: { bg: "bg-zenith-blue-500/10", border: "border-zenith-blue-500/30", icon: "text-zenith-blue-700" },
};
function SummaryCard({ label, value, icon: Icon, tone, sub }) {
  const t = TONE[tone] || TONE.teal;
  return (
    <div className={`border ${t.border} ${t.bg} rounded-sm p-4 flex items-center gap-3`}>
      <div className={`w-9 h-9 grid place-items-center rounded-sm bg-white ${t.border} border`}>
        <Icon size={18} weight="duotone" className={t.icon} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">{label}</div>
        <div className="font-display text-2xl font-bold text-adb-navy leading-none">{value ?? "—"}{sub && <span className="text-xs text-zinc-400 ml-1">{sub}</span>}</div>
      </div>
    </div>
  );
}
